import * as _ from 'lodash-es';
import { BlockEmbed } from 'quill/blots/block';
import type { AttributeMap } from 'quill/core';
import { Delta, Parchment } from 'quill/core';
import React from 'react';
import { createPortal } from 'react-dom';
import { isApolloError } from '@apollo/client';
import { EntityUploadType } from '../../graphql/generated/graphql';
import { editorToasts } from '../../toasts/toasts';
import Logger from '../../utils/Logger';
import normalizeGraphQLError, {
  ErrorCodes,
} from '../../utils/error/normalizeGraphQLError';
import safeParseUrl from '../../utils/url/safeParseUrl';
import {
  getAssetTokenFromBlot,
  getSourceUrlWithAssetToken,
} from '../AssetTokenContext';
import AssetUploader from '../AssetUploader';
import ImageCarousel from '../image/ImageCarousel';
import type { ImageCarouselHeight } from '../image/ImageCarouselHeight';
import { isImageCarouselHeight } from '../image/ImageCarouselHeight';
import { CAROUSEL_IMAGE_ATTRIBUTE, getImageSrc } from '../image/ImageNode';
import { isUploadedCarouselItem } from '../image/types';
import type {
  CarouselItem,
  UploadedCarouselItem,
  UploadingCarouselItem,
} from '../image/types';
import Queue from '../utils/Queue';
import { findBlotByNode } from '../utils/findByNode';
import generateDeltaRandomId from '../utils/generateDeltaRandomId';
import { getImageUploadsForFiles } from '../utils/getImagePlaceholderInfo';
import showEditorToast from '../utils/showEditorToast';
import Comment from './Comment';
import Diff from './Diff';
import Height from './Height';
import type { Store } from './ReactBlockEmbed';
import { createStore } from './ReactBlockEmbed';
import Reaction from './Reaction';
import Scroll from './Scroll';
import type { SpanValue } from './Span';
import Span from './Span';
import UserSequence from './UserSequence';
import VersionChange from './VersionChange';

const normalizeAttributeValue = (name: string, value: unknown) => {
  if (name === 'width' || name === 'height') {
    // Percentage values are invalid as width/height property for <img/> in HTML.
    return Number(value) || undefined;
  }

  return String(value);
};

export const compactCarouselItem = (item: CarouselItem): CarouselItem => {
  if (isUploadedCarouselItem(item)) {
    const { source, ...rest } = item;
    return { source, ..._.omitBy(rest, _.isNil) };
  }
  const { uploadPayload, ...rest } = item;
  return { uploadPayload, ..._.omitBy(rest, _.isNil) };
};

Delta.registerEmbed('image', {
  compose(_a: CarouselItem[], b: CarouselItem[]) {
    return b;
  },
  transform(a: CarouselItem[], b: CarouselItem[], priority: boolean) {
    return priority ? b : a;
  },
  invert(_change: CarouselItem[], base: CarouselItem[]) {
    return base;
  },
});

type UploadPayload = UploadingCarouselItem['uploadPayload'];
type ImageValue =
  | CarouselItem[] // New format
  | string // Legacy format
  | true; // Placeholder indicating the image is being uploaded by a collaborator

export type FloatValue = 'left' | 'right' | null;

interface State {
  value: CarouselItem[] | true;
  height?: ImageCarouselHeight | null;
  span?: SpanValue | null;
  float?: FloatValue;
  selectedIndex: number;
  hoverFileIndex: number | null;
}

function attributesToString(attributes: NamedNodeMap) {
  return [...attributes]
    .map((attr) => `${attr.nodeName}="${attr.nodeValue ?? ''}"`)
    .join(' ');
}

function carouselItemToHtml(
  { source, caption, natural }: UploadedCarouselItem,
  assetToken: string | null,
  attributes = '',
) {
  const captionHTML = caption
    ? `<figcaption>${_.escape(caption)}</figcaption>`
    : '';

  let size = '';
  if (natural) {
    size = ` data-natural-width="${natural.width}" data-natural-height="${natural.height}"`;
  }

  return `<figure${
    attributes ? ` ${attributes}` : ''
  }><img src="${getSourceUrlWithAssetToken(source, assetToken)}"${size} />${captionHTML}</figure>`;
}

class ImageBlot extends BlockEmbed {
  static blotName = 'image';
  static className = 'ql-image';
  static tagName = 'FIGURE';

  static ALLOWED_ATTRIBUTES = ['float'];
  static ALLOWED_PROPERTIES = ['width', 'caption'];

  static isImageNode(node: EventTarget): node is HTMLElement {
    return (
      node instanceof HTMLImageElement &&
      node.hasAttribute(CAROUSEL_IMAGE_ATTRIBUTE)
    );
  }

  static addImageIdsIfNeeded(images: CarouselItem[]) {
    return images.length > 1
      ? images.map((item) => {
          if (item.id == null) {
            return {
              ...item,
              id: generateDeltaRandomId(),
            };
          }
          return item;
        })
      : images;
  }

  static filterUnSupportedProperties(value: CarouselItem[]): CarouselItem[] {
    return value.map((v) => {
      const commonFields = {
        id: v.id,
        natural: v.natural,
        width: this.ALLOWED_PROPERTIES.includes('width') ? v.width : undefined,
        caption: this.ALLOWED_PROPERTIES.includes('caption')
          ? v.caption
          : undefined,
      } as const satisfies Partial<CarouselItem>;

      return compactCarouselItem(
        isUploadedCarouselItem(v)
          ? { ...commonFields, source: v.source }
          : {
              ...commonFields,
              uploadPayload: v.uploadPayload,
              uploadPreviewSource: v.uploadPreviewSource,
            },
      );
    });
  }

  private static imageUploadQueue: Queue<string | null> = new Queue();

  async upload(uploadPayload: UploadPayload, domNode: HTMLElement) {
    let uploadedURL: string | null = null;

    try {
      const assetUploader = AssetUploader.getForBlot(this);
      if (!assetUploader) {
        throw new Error('Unable to find assetUploader module.');
      }
      uploadedURL = await ImageBlot.imageUploadQueue.enqueue(() =>
        assetUploader.sendFileToServer(uploadPayload, EntityUploadType.Image),
      );
    } catch (err) {
      const blot = findImageBlotByNode(domNode);
      if (blot) {
        const isUploadPayloadUrl =
          typeof uploadPayload === 'string' &&
          safeParseUrl(uploadPayload)?.protocol === 'https:';

        const canKeepImage = isUploadPayloadUrl;

        const isUnprocessable =
          err instanceof Error &&
          isApolloError(err) &&
          err.graphQLErrors
            ?.map(normalizeGraphQLError)
            .some(({ code }) => code === ErrorCodes.UNPROCESSABLE_ENTITY);

        if (isUnprocessable) {
          showEditorToast(editorToasts.imageCopyError);
        } else if (!canKeepImage) {
          if (err.networkError && err.networkError.statusCode === 413) {
            showEditorToast(editorToasts.imageSizeExceededError);
          } else if (isMSWordImage(uploadPayload)) {
            ImageBlot.showMSWordEditorToastThrottled();
          } else {
            showEditorToast(editorToasts.genericImageUploadError);
          }
        }

        if (canKeepImage) {
          blot.cleanUpload(uploadPayload, uploadPayload);
        } else if (blot.isCarousel()) {
          blot.removeImageWithUploadPayloadFromCarousel(uploadPayload);
        } else {
          blot.remove();
        }
      }
    }

    if (uploadedURL) {
      findImageBlotByNode(domNode)?.cleanUpload(uploadPayload, uploadedURL);
    }
  }

  static showMSWordEditorToastThrottled = _.throttle(
    () => {
      showEditorToast(editorToasts.msWordWarning);
    },
    1000,
    { leading: true, trailing: false },
  );

  static create(value: ImageValue) {
    if (typeof value === 'string') {
      Logger.warn('Received legacy image value');
    }

    const figure = super.create(value) as HTMLElement;
    figure.setAttribute('contenteditable', 'false');

    if (value === true) {
      // This happens when other collaborator is uploading the image.
      figure.setAttribute('data-uploading-placeholder', '');
    }

    return figure;
  }

  static formats(domNode: HTMLElement) {
    const styleFormats: AttributeMap = {};
    if (domNode.tagName === 'IMG') {
      if (typeof domNode.style.float === 'string') {
        const float = domNode.style.float.toLowerCase();
        if (float === 'left' || float === 'right') {
          styleFormats.float = float;
        }
      }
    }
    const attributeFormats = this.ALLOWED_ATTRIBUTES.reduce(
      (formats, name) => {
        const attribute = `data-${name}`;
        if (!domNode.hasAttribute(attribute)) {
          return formats;
        }
        const value = domNode.getAttribute(attribute);
        return value
          ? {
              ...formats,
              [name]: normalizeAttributeValue(name, value),
            }
          : formats;
      },
      {} as Record<string, unknown>,
    );

    return { ...styleFormats, ...attributeFormats };
  }

  static match(url: string) {
    return /\.(jpe?g|gif|png|svg)$/.test(url);
  }

  private static figureToCarouselItem(figure: Element) {
    const imgNode = figure.querySelector('img');
    if (!imgNode) return null;
    const figcaptionNode = figure.querySelector('figcaption');
    const src = imgNode.getAttribute('src');
    if (!src) return null;

    const image: CarouselItem = { id: generateDeltaRandomId(), source: src };
    if (this.ALLOWED_PROPERTIES.includes('caption')) {
      const caption = figcaptionNode?.innerText ?? figcaptionNode?.textContent;
      if (caption) {
        image.caption = caption;
      }
    }
    const width = Number(imgNode.getAttribute('data-natural-width'));
    const height = Number(imgNode.getAttribute('data-natural-height'));
    if (
      !Number.isNaN(width) &&
      !Number.isNaN(height) &&
      width > 0 &&
      height > 0
    ) {
      image.natural = { width, height };
    }
    return image;
  }

  static value(domNode: HTMLElement): UploadedCarouselItem[] | null {
    if (domNode.tagName === 'IMG') {
      const source = domNode.getAttribute('src');
      return source ? [{ source }] : null;
    }

    if (domNode.tagName === ImageBlot.tagName) {
      const nestedFigures = domNode.querySelectorAll(':scope figure');
      if (nestedFigures.length > 0) {
        return _.compact(
          Array.from(nestedFigures).map(this.figureToCarouselItem.bind(this)),
        );
      }

      const carouselItem = this.figureToCarouselItem(domNode);
      return carouselItem ? [_.omit(carouselItem, 'id')] : null;
    }

    return null;
  }

  private id = _.uniqueId();
  private store: Store<State>;

  constructor(scroll: Parchment.ScrollBlot, domNode: Node, value: ImageValue) {
    // @ts-expect-error
    super(scroll, domNode, value);

    let normalizedValue: CarouselItem[] | true = true;
    if (Array.isArray(value)) {
      normalizedValue = this.statics.filterUnSupportedProperties(value);
    } else if (typeof value === 'string') {
      normalizedValue = [{ source: value }];
    }
    this.store = createStore<State>({
      value: normalizedValue,
      selectedIndex: 0,
      hoverFileIndex: null,
    });

    if (Array.isArray(normalizedValue)) {
      normalizedValue.forEach((item) => {
        if (!isUploadedCarouselItem(item)) {
          this.upload(item.uploadPayload, this.domNode);
        }
      });
    }
  }

  value(): { image: CarouselItem[] | true } {
    const { images } = this.getData();
    return { image: images.length > 0 ? images : true };
  }

  attach() {
    super.attach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_MOUNT, this);
  }

  detach() {
    super.detach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_UNMOUNT, this);
  }

  formats() {
    return {
      ...this.attributes?.values(),
      ...this.statics.formats(this.domNode, this.scroll),
    };
  }

  format(name: string, value: unknown) {
    if (
      [
        Comment.attrName,
        Reaction.attrName,
        Span.attrName,
        Height.attrName,
        Diff.attrName,
        VersionChange.attrName,
        UserSequence.attrName,
      ].includes(name)
    ) {
      const attributor = this.scroll.query(name, Parchment.Scope.ATTRIBUTE);
      if (attributor) {
        // @ts-expect-error
        this.attributes.attribute(attributor, value);
      }

      if (
        name === Height.attrName &&
        (value === null || isImageCarouselHeight(value))
      ) {
        this.store.setState({ height: value });
      }
      if (name === Span.attrName) {
        this.store.setState({ span: value as State['span'] });
      }
    } else if (this.statics.ALLOWED_ATTRIBUTES.includes(name)) {
      const attribute = `data-${name}`;
      if (value) {
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        this.domNode.setAttribute(attribute, String(value));
      } else {
        this.domNode.removeAttribute(attribute);
      }
      if (name === 'float') {
        this.store.setState({ float: value as FloatValue });
      }
    } else {
      super.format(name, value);
    }
  }

  getReferenceNodeForBounds() {
    return (
      this.domNode.querySelector('.image-container') ||
      this.domNode.querySelector('img') ||
      this.domNode
    );
  }

  getData(): { images: UploadedCarouselItem[]; selectedIndex: number } {
    const { value, selectedIndex } = this.store.getState();
    if (!Array.isArray(value)) return { images: [], selectedIndex: 0 };

    const selectedImage = value[selectedIndex];

    const uploadedImages = value.filter(isUploadedCarouselItem);
    const selectedIndexInUploadedImages = uploadedImages.findIndex(
      (image) => image === selectedImage,
    );
    return {
      images: uploadedImages,
      selectedIndex:
        selectedIndexInUploadedImages >= 0 ? selectedIndexInUploadedImages : 0,
    };
  }

  html() {
    const { images } = this.getData();

    const firstImage = images[0];
    if (!firstImage) return '';

    const assetToken = getAssetTokenFromBlot(this);
    const attributes = attributesToString(this.domNode.attributes);

    return images.length === 1
      ? carouselItemToHtml(firstImage, assetToken, attributes)
      : `<figure ${attributes}>${images
          .map((item) => carouselItemToHtml(item, assetToken))
          .join('')}</figure>`;
  }

  isUploading() {
    const { value } = this.store.getState();
    return (
      !Array.isArray(value) ||
      value.some((item) => !isUploadedCarouselItem(item))
    );
  }

  updateContent(value: CarouselItem[]) {
    this.store.setState({
      value: this.statics.filterUnSupportedProperties(value),
    });
  }

  private removeImageWithUploadPayloadFromCarousel(
    uploadPayload: UploadPayload,
  ) {
    const images = this.store.getState().value;

    if (Array.isArray(images)) {
      const newValue = images.filter(
        (image) =>
          isUploadedCarouselItem(image) ||
          image.uploadPayload !== uploadPayload,
      );

      this.handleUserChange(newValue);
    }
  }

  private async cleanUpload(uploadPayload: UploadPayload, src: string) {
    let natural: { width: number; height: number } | undefined;
    try {
      const assetToken = getAssetTokenFromBlot(this);

      // Pre cache image so when we update the src, users won't see a flash
      const size = await preCacheImage(getImageSrc(src, assetToken));

      // size could be zeros if the image doesn't have an intrinsic size
      if (size.width > 0 && size.height > 0) {
        natural = size;
      }
    } finally {
      const images = this.store.getState().value;
      if (Array.isArray(images)) {
        const newValue = images.map((image) => {
          return isUploadedCarouselItem(image) ||
            image.uploadPayload !== uploadPayload
            ? image
            : compactCarouselItem({
                id: image.id,
                source: src,
                natural,
                caption: image.caption,
                width: image.width,
              } as const satisfies UploadedCarouselItem);
        });

        this.handleUserChange(newValue);
      } else {
        const newValue = [
          compactCarouselItem({
            id: generateDeltaRandomId(),
            ...getFirstImage(this.store.getState()),
            source: src,
            uploadPayload: undefined,
          }),
        ];
        this.handleUserChange(newValue);
      }
    }
  }

  private handleUserChange(
    newValue: CarouselItem[],
    resetHoverFileIndex = false,
  ) {
    const value = this.statics.addImageIdsIfNeeded(newValue);

    const state = this.store.getState();
    let { selectedIndex } = state;
    const { value: currentValue } = state;

    if (Array.isArray(currentValue) && Array.isArray(value)) {
      const selectedImage = currentValue[selectedIndex];

      if (selectedImage) {
        const selectedImageInNewValue = newValue.indexOf(selectedImage);
        if (selectedImageInNewValue === -1) {
          selectedIndex = Math.min(selectedIndex, value.length - 1);
        } else {
          selectedIndex = selectedImageInNewValue;
        }
      }
    }

    this.store.setState({
      value,
      selectedIndex,
      hoverFileIndex: resetHoverFileIndex ? null : state.hoverFileIndex,
    });

    const uploadedItems = value.filter(isUploadedCarouselItem);
    const nextValue = uploadedItems.length > 0 ? uploadedItems : true;
    if (!_.isEqual(currentValue, nextValue)) {
      // @ts-expect-error
      this.scroll.emitEmbedUpdate(this, nextValue);
    }
  }

  uploadFiles = (files: File[]) => {
    const state = this.store.getState();
    const images = Array.isArray(state.value) ? state.value : null;
    if (!images) return;

    const placeholders = files.map<UploadingCarouselItem>((file) => ({
      id: generateDeltaRandomId(),
      uploadPayload: file,
    }));

    const index = state.hoverFileIndex;
    const nextImages: CarouselItem[] =
      index != null
        ? images.flatMap((item, i) => {
            if (i === images.length - 1 && index === images.length) {
              return [item, ...placeholders];
            }
            if (i === index) {
              return [...placeholders, item];
            }
            return item;
          })
        : [...images, ...placeholders];

    placeholders.forEach((placeholder) => {
      if (placeholder.uploadPayload) {
        this.upload(placeholder.uploadPayload, this.domNode);
      }
    });

    const attributes = this.attributes?.values() ?? {};

    if (attributes.span === 'half') {
      this.format('span', null);
    }

    this.format('float', null);

    const nextImagesWithoutWidth = nextImages.map((image) =>
      'width' in image
        ? compactCarouselItem({ ...image, width: undefined })
        : image,
    );

    this.handleUserChange(nextImagesWithoutWidth, true);

    getImageUploadsForFiles(files).then((items) => {
      const currentValue = this.store.getState().value;
      if (Array.isArray(currentValue)) {
        this.handleUserChange(
          currentValue.map((image) => {
            const placeholder =
              !isUploadedCarouselItem(image) &&
              items.find((item) => item.uploadPayload === image.uploadPayload);
            return placeholder
              ? {
                  ...image,
                  uploadPreviewSource: placeholder.uploadPreviewSource,
                  natural: placeholder.natural,
                }
              : image;
          }),
        );
      }
    });
  };

  public isCarousel() {
    const state = this.store.getState();
    const images = Array.isArray(state.value) ? state.value : null;
    const formats = this.formats();

    if (!images || formats.float != null) return false;
    return images.length > 1;
  }

  private onSelectedIndexChange = (index: number) => {
    this.store.setState({ selectedIndex: index });
  };

  private onHoverFileIndexChange = (index: number | null) => {
    this.store.setState({ hoverFileIndex: index });
  };

  private onRemove = (image: CarouselItem) => {
    const { value } = this.store.getState();
    if (!Array.isArray(value) || !value.includes(image)) return;

    this.handleUserChange(value.filter((item) => item !== image));
  };

  private onReorder = (images: CarouselItem[]) => {
    this.handleUserChange(images);
  };

  private onCaptionChange = (image: CarouselItem, caption: string) => {
    const { value } = this.store.getState();
    if (!Array.isArray(value) || !value.includes(image)) return;

    this.handleUserChange(
      value.map((item) => {
        if (item === image) {
          return compactCarouselItem({
            ...item,
            caption: caption === '' ? undefined : caption,
          });
        }
        return item;
      }),
    );
  };

  renderPortal({ selected }: { selected: boolean }) {
    const { Connect } = this.store;
    const key = `editor_${this.statics.blotName}_${this.id}`;
    const isCaptionsAllowed =
      this.statics.ALLOWED_PROPERTIES.includes('caption');

    return createPortal(
      <Connect>
        {(state) => {
          const images = Array.isArray(state.value) ? state.value : null;
          if (!images) return null;

          return (
            <ImageCarousel
              selected={selected}
              images={images}
              onReorder={this.onReorder}
              onRemove={this.onRemove}
              onCaptionChange={this.onCaptionChange}
              selectedIndex={state.selectedIndex}
              onSelectedIndexChange={this.onSelectedIndexChange}
              height={state.height ?? 'default'}
              span={state.span ?? null}
              float={state.float ?? null}
              captionsAllowed={isCaptionsAllowed}
              onAddImages={this.uploadFiles}
              hoverFileIndex={state.hoverFileIndex}
              onHoverFileIndexChange={this.onHoverFileIndexChange}
            />
          );
        }}
      </Connect>,
      this.domNode,

      // Hint to the reconciler otherwise the whole component will be torn down and re-created.
      // This is useful to prevent the CSS animation of progress bar from restarting.
      key,
    );
  }
}

function findImageBlotByNode(node: Node) {
  const blot = findBlotByNode(node);
  return blot instanceof ImageBlot ? blot : null;
}

function getFirstImage({ value }: State) {
  return Array.isArray(value) ? value[0] : null;
}

function preCacheImage(url: string) {
  const img = new Image();
  img.src = url;

  return Promise.race<{ width: number; height: number }>([
    img
      .decode()
      .then(() => ({ width: img.naturalWidth, height: img.naturalHeight })),
    new Promise((_resolve, reject) => {
      setTimeout(
        () => reject(new Error('Image load exceeded timeout')),
        20 * 1000,
      );
    }),
  ]);
}

function isMSWordImage(uploadPayload: UploadPayload) {
  return (
    typeof uploadPayload === 'string' &&
    uploadPayload.startsWith('file://') &&
    uploadPayload.includes('msohtmlclip')
  );
}

export default ImageBlot;
